/**
 * JobIntegritySection Component - Phase 3 Refinement
 * 
 * Visual hierarchy optimized for:
 * - Clean jobs are visually quiet (minimal/hidden)
 * - Blocked/review states clearly differentiated
 * - Suggestions de-emphasized
 * - Pricing content remains primary
 * - Compact collapsed-card scan quality
 * 
 * Tiers:
 * - Required (Tier 1): Critical, blocking if missing
 * - Default (Tier 2): Auto-included, removable with reason
 * - Suggested (Tier 3): Optional, low visual weight
 */

import React, { useState, useMemo } from 'react';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Plus, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  RotateCcw,
  X,
  Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

// Removal reason presets - shortened labels
const REMOVAL_PRESETS = [
  { value: 'customer_declined', label: 'Customer declined' },
  { value: 'not_required_for_this_job', label: 'Not needed' },
  { value: 'already_recently_replaced', label: 'Recently replaced' },
  { value: 'not_appropriate_for_this_vehicle', label: 'Not for this vehicle' },
  { value: 'included_elsewhere', label: 'Included elsewhere' },
  { value: 'pricing_adjustment_goodwill', label: 'Goodwill adjustment' },
  { value: 'other', label: 'Other' },
];

/**
 * Status Badge - Compact, scannable job state indicator
 */
export const JobIntegrityBadge = ({ state, issueCount = 0, compact = false }) => {
  const config = {
    complete: {
      icon: CheckCircle,
      label: 'OK',
      className: 'bg-green-50 text-green-600 border-green-200',
      iconClass: 'text-green-500'
    },
    needs_review: {
      icon: AlertTriangle,
      label: 'Review',
      className: 'bg-amber-50 text-amber-600 border-amber-200',
      iconClass: 'text-amber-500'
    },
    blocked: {
      icon: XCircle,
      label: 'Blocked',
      className: 'bg-red-50 text-red-600 border-red-200',
      iconClass: 'text-red-500'
    }
  };

  const badge = config[state] || config.complete;
  const Icon = badge.icon;

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border ${badge.className}`}>
        <Icon className={`w-2.5 h-2.5 ${badge.iconClass}`} />
        {issueCount > 0 && <span>{issueCount}</span>}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${badge.className}`}>
      <Icon className={`w-3 h-3 ${badge.iconClass}`} />
      <span>{badge.label}</span>
    </span>
  );
};

/**
 * Integrity Item Row - Minimal, action-oriented
 */
const IntegrityItemRow = ({ 
  item, 
  tier, 
  onAdd, 
  onRemove, 
  onRestore,
  isPresent,
  isRemoved,
  isLocked 
}) => {
  const isMissing = !isPresent && !isRemoved;
  const isMissingRequired = isMissing && tier === 'required';

  return (
    <div 
      className={`flex items-center justify-between py-1.5 px-2 rounded border group transition-all ${
        isRemoved ? 'opacity-40 bg-slate-50/60 border-slate-100' :
        isMissingRequired ? 'bg-red-50 border-red-200 hover:bg-red-100 cursor-pointer' :
        isMissing ? 'bg-slate-50/60 border-slate-100 hover:bg-green-50 hover:border-green-200 cursor-pointer' :
        'bg-slate-50/60 border-slate-100'
      }`}
      onClick={() => {
        if (isMissing && onAdd) onAdd(item);
      }}
      data-testid={`integrity-item-${item.key || item.label}`}
    >
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        {isPresent ? (
          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
        ) : isRemoved ? (
          <Trash2 className="w-3 h-3 text-slate-300 flex-shrink-0" />
        ) : tier === 'suggested' ? (
          <Lightbulb className="w-3 h-3 text-slate-300 flex-shrink-0" />
        ) : (
          <div className={`w-2.5 h-2.5 rounded-full border-2 flex-shrink-0 ${
            tier === 'required' ? 'border-red-300' : 'border-slate-300'
          }`} />
        )}
        
        <span className={`text-xs truncate ${isRemoved ? 'line-through' : ''}`}>
          {item.label}
        </span>
        
        {/* Inline "tap to add" hint for missing items */}
        {isMissing && (
          <span className={`text-[10px] font-medium ml-1 opacity-0 group-hover:opacity-100 transition-opacity ${
            isMissingRequired ? 'text-red-500' : 'text-green-600'
          }`}>
            tap to add
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {item.unit_price > 0 && (
          <span className="text-[10px] text-slate-400">
            £{item.unit_price.toFixed(0)}
          </span>
        )}
        
        {item.charge_amount > 0 && (
          <span className="text-[10px] text-slate-400">
            £{item.charge_amount.toFixed(2)}
          </span>
        )}
        
        {/* Show Add button for ANY missing item (not just suggestions) */}
        {!isPresent && !isRemoved && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onAdd?.(item); }}
            className={`h-6 w-6 p-0 ${
              tier === 'required' 
                ? 'text-red-500 hover:text-red-600 hover:bg-red-50' 
                : 'text-green-500 hover:text-green-600 hover:bg-green-50'
            }`}
            title={`Add ${item.label}`}
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        )}
        
        {isPresent && !isLocked && tier !== 'required' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove?.(item)}
            className="h-5 w-5 p-0 text-slate-300 hover:text-red-500 hover:bg-red-50"
          >
            <X className="w-3 h-3" />
          </Button>
        )}
        
        {isRemoved && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRestore?.(item)}
            className="h-5 w-5 p-0 text-slate-400 hover:text-blue-500 hover:bg-blue-50"
          >
            <RotateCcw className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * Removal Reason Modal - Streamlined
 */
const RemovalReasonModal = ({ 
  isOpen, 
  onClose, 
  item, 
  tier,
  onConfirm 
}) => {
  const [reasonPreset, setReasonPreset] = useState('');
  const [reasonCustom, setReasonCustom] = useState('');
  
  const isRequired = tier === 'required';
  const canConfirm = reasonPreset && (isRequired ? reasonCustom.length >= 10 : true);

  const handleConfirm = () => {
    onConfirm({
      item,
      reasonPreset,
      reasonCustom: reasonCustom || null
    });
    setReasonPreset('');
    setReasonCustom('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {isRequired ? (
              <>
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Override Required
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 text-slate-400" />
                Remove Item
              </>
            )}
          </DialogTitle>
          {isRequired && (
            <DialogDescription className="text-red-600 text-sm">
              Removing required items may block the estimate.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-3 py-3">
          <div className="p-2 bg-slate-50 rounded text-sm font-medium">
            {item?.label}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Reason</Label>
            <Select value={reasonPreset} onValueChange={setReasonPreset}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select reason..." />
              </SelectTrigger>
              <SelectContent>
                {REMOVAL_PRESETS.map(preset => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(isRequired || reasonPreset === 'other') && (
            <div className="space-y-1.5">
              <Label className="text-xs">
                Details {isRequired && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                value={reasonCustom}
                onChange={(e) => setReasonCustom(e.target.value)}
                placeholder={isRequired ? "Explain why (min 10 chars)..." : "Optional details..."}
                className="min-h-[60px] text-sm"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            size="sm"
            onClick={handleConfirm} 
            disabled={!canConfirm}
            className={isRequired ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            {isRequired ? 'Override' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Job Integrity Section - Visually quiet when clean
 */
export const JobIntegritySection = ({
  jobGroup,
  integrityData,
  onAddItem,
  onRemoveItem,
  onRestoreItem,
  isExpanded = true,
  isLoading = false
}) => {
  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Calculate display state
  const displayState = useMemo(() => {
    if (!integrityData) return { isClean: true, hasIssues: false, hasSuggestions: false };
    
    const isClean = integrityData.state === 'complete' && 
                    integrityData.suggestion_count === 0 &&
                    integrityData.blocking_issues === 0 &&
                    integrityData.review_issues === 0;
    
    return {
      isClean,
      hasIssues: integrityData.blocking_issues > 0 || integrityData.review_issues > 0,
      hasSuggestions: integrityData.suggestion_count > 0,
      isBlocked: integrityData.state === 'blocked',
      needsReview: integrityData.state === 'needs_review'
    };
  }, [integrityData]);

  // Clean jobs: show nothing
  if (displayState.isClean) {
    return null;
  }

  const handleRemoveClick = (item, tier) => {
    setItemToRemove({ ...item, tier });
    setShowRemovalModal(true);
  };

  const handleConfirmRemoval = (data) => {
    onRemoveItem?.(data.item, data.reasonPreset, data.reasonCustom);
  };

  if (!integrityData) return null;

  const { required_items, default_items, suggested_items } = integrityData;
  
  // Filter items by state
  const missingRequired = required_items?.items?.filter(i => !i.is_present && !i.removed) || [];
  const removedDefaults = default_items?.items?.filter(i => i.removed) || [];
  const presentDefaults = default_items?.items?.filter(i => i.is_present && !i.removed) || [];
  const availableSuggestions = suggested_items?.items?.filter(i => !i.is_present) || [];
  const quantityWarnings = integrityData.quantity_warnings || [];

  // Only show sections that have content
  const hasBlockingContent = missingRequired.length > 0;
  const hasReviewContent = removedDefaults.length > 0 || !integrityData.labour_valid || quantityWarnings.length > 0;
  const hasSuggestionContent = availableSuggestions.length > 0;

  return (
    <div className="mt-2 pt-2 border-t border-slate-100/80">
      <div className="space-y-1.5">
        
        {/* Blocking Issues - Most prominent */}
        {hasBlockingContent && (
          <div className="rounded border border-red-200 bg-red-50/50 overflow-hidden">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-red-100/50">
              <XCircle className="w-3.5 h-3.5 text-red-500" />
              <span className="text-xs font-semibold text-red-700">
                Missing ({missingRequired.length})
              </span>
            </div>
            <div className="p-1.5 space-y-1">
              {missingRequired.map((item, idx) => (
                <IntegrityItemRow
                  key={item.code || idx}
                  item={item}
                  tier="required"
                  isPresent={false}
                  isRemoved={false}
                  isLocked={item.metadata?.locked}
                  onAdd={onAddItem}
                />
              ))}
            </div>
          </div>
        )}

        {/* Review Items - Medium prominence */}
        {hasReviewContent && (
          <div className="rounded border border-amber-200 bg-amber-50/30 overflow-hidden">
            <div className="flex items-center gap-2 px-2 py-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-medium text-amber-700">Review</span>
            </div>
            <div className="p-1.5 space-y-1">
              {/* Labour warning */}
              {!integrityData.labour_valid && integrityData.labour_warning && (
                <div className="text-xs text-amber-600 px-2 py-1 bg-amber-50 rounded">
                  {integrityData.labour_warning}
                </div>
              )}
              {/* Pair quantity warnings */}
              {quantityWarnings.map((warning, idx) => (
                <div key={`qty-${idx}`} className="text-xs text-amber-600 px-2 py-1 bg-amber-50 rounded flex items-start gap-1.5">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>{warning}</span>
                </div>
              ))}
              {/* Removed defaults */}
              {removedDefaults.map((item, idx) => (
                <IntegrityItemRow
                  key={item.code || idx}
                  item={item}
                  tier="default"
                  isPresent={false}
                  isRemoved={true}
                  isLocked={false}
                  onRestore={onRestoreItem}
                />
              ))}
            </div>
          </div>
        )}

        {/* Default Included - Low prominence, collapsible */}
        {presentDefaults.length > 0 && (
          <div className="text-xs text-slate-400 px-1">
            <span className="font-medium">{presentDefaults.length} default</span> item{presentDefaults.length !== 1 ? 's' : ''} included
          </div>
        )}

        {/* Suggestions - Minimal prominence, collapsed by default */}
        {hasSuggestionContent && (
          <div className="border-t border-dashed border-slate-100 pt-1.5 mt-1">
            <button
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors w-full"
              onClick={() => setShowSuggestions(!showSuggestions)}
            >
              <Lightbulb className="w-3 h-3" />
              <span>{availableSuggestions.length} suggestion{availableSuggestions.length !== 1 ? 's' : ''}</span>
              {showSuggestions ? (
                <ChevronDown className="w-3 h-3 ml-auto" />
              ) : (
                <ChevronRight className="w-3 h-3 ml-auto" />
              )}
            </button>
            {showSuggestions && (
              <div className="mt-1.5 space-y-1 animate-in fade-in duration-150">
                {availableSuggestions.map((item, idx) => (
                  <IntegrityItemRow
                    key={item.code || idx}
                    item={item}
                    tier="suggested"
                    isPresent={false}
                    isRemoved={false}
                    isLocked={false}
                    onAdd={onAddItem}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Removal Modal */}
      <RemovalReasonModal
        isOpen={showRemovalModal}
        onClose={() => {
          setShowRemovalModal(false);
          setItemToRemove(null);
        }}
        item={itemToRemove}
        tier={itemToRemove?.tier}
        onConfirm={handleConfirmRemoval}
      />
    </div>
  );
};

/**
 * Collapsed Card Status - Ultra-compact for card headers
 * Only shows when there are actionable issues
 */
export const CollapsedIntegrityStatus = ({ integrityData }) => {
  if (!integrityData) return null;

  const { state, blocking_issues, review_issues, suggestion_count } = integrityData;
  
  // Clean complete jobs: show nothing
  if (state === 'complete' && suggestion_count === 0) {
    return null;
  }

  const totalIssues = (blocking_issues || 0) + (review_issues || 0);

  // Blocked - most prominent
  if (state === 'blocked') {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-100 text-red-600 text-[10px] font-semibold border border-red-200">
        <XCircle className="w-2.5 h-2.5" />
        {totalIssues > 0 ? totalIssues : '!'}
      </span>
    );
  }

  // Needs review - medium
  if (state === 'needs_review') {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 text-[10px] font-medium border border-amber-200">
        <AlertTriangle className="w-2.5 h-2.5" />
        {totalIssues > 0 && totalIssues}
      </span>
    );
  }

  // Complete with suggestions - subtle
  if (suggestion_count > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-slate-400 text-[10px]">
        <Lightbulb className="w-2.5 h-2.5" />
        {suggestion_count}
      </span>
    );
  }

  return null;
};

export default JobIntegritySection;
